<template>
    <ui-report title="AFPs Due"
               subtitle="A list of projects that need AFPs raising either because they are due or because the works complete is greater than the amount previously applied for. Note: If a package has no application dates, it will never show here."
               :no-filters="true"
               grid="mt-6 gap-x-8 gap-y-4 grid-cols-12"
    >
        <template #default="{ filters }">
            <accounts-sales-afps-stat
                :value="formatTotal(applyFor)"
                :label="`To Apply For (${applyForCount})`"
                :filter="`applyFor`"
                :enabled="show.applyFor"
                icon="far fa-file-chart-line"
                class="col-span-4"
                v-on:afp-stat-changed="afpStatChanged"
            ></accounts-sales-afps-stat>
            <accounts-sales-afps-stat
                :value="formatTotal(uncertified)"
                :label="`Uncertified (${uncertifiedCount})`"
                :filter="`uncertified`"
                :enabled="show.uncertified"
                icon="far fa-file-chart-line"
                class="col-span-4"
                v-on:afp-stat-changed="afpStatChanged"
            ></accounts-sales-afps-stat>
            <accounts-sales-afps-stat
                :value="formatTotal(retentionHeld)"
                :label="`Retention Held (${retentionHeldCount})`"
                :filter="`retentionHeld`"
                :enabled="show.retentionHeld"
                icon="far fa-sigma"
                class="col-span-4"
                v-on:afp-stat-changed="afpStatChanged"
            ></accounts-sales-afps-stat>
            <div class="col-span-12 text-right">
                <div class="relative inline-block w-72">
                    <ui-input class="m-0" field="bg-gray-50 pr-10 " name="query"
                              v-model="query"></ui-input>
                    <div
                        class="absolute right-0 inset-y-0 h-full flex items-center justify-center p-3 text-gray-400">
                        <i class="fas fa-search"></i>
                    </div>
                </div>
            </div>
            <ui-report-panel :filters="filters" :fetch="fetch" @filtersChanged="({ refresh }) => refresh()"
                             v-slot="{ report }" class="overflow-hidden">
                <ui-table v-if="report && report.length > 0"
                          :headers="['Package', 'Application due date', 'Last PR', 'Last AFP','Status']"
                          v-slot="{ cellClass }">
                    <template v-for="afpGroup in report">
                        <tr :key="afpGroup.key">
                            <td colspan="7" v-text="afpGroup.label"
                                class="px-6 py-2 border-b border-t bg-gray-50 text-gray-500 font-semibold text-xs uppercase tracking-widest"></td>
                        </tr>
                        <tr v-for="afp in afpGroup.afps" v-if="isNotFiltered(afp)" :key="afpGroup.key+'|'+afp.key()"
                            class="hover:bg-blue-50 cursor-pointer" @click="goTo(afp._links.frontend.view)">
                            <td :class="cellClass" class="align-top max-w-sm truncate" :title="afp.package_name"
                                v-text="afp.package_name"></td>
                            <td :class="cellClass" class="align-top">
                                <template v-if="afp.has_due_dates">
                                    <div class="font-bold">{{ afp.application_due_date_formatted }}</div>
                                    <div :class="afp.due_in_class" class="text-sm">{{ afp.due_in }}</div>
                                    <div v-if="!afp.has_future_dates" class="font-bold text-gray-400 mt-3 text-xs">
                                        Missing Future Dates
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="font-bold">No Application Dates Set</div>
                                </template>
                            </td>
                            <td :class="cellClass" class="align-top">
                                <div class="font-bold">{{ afp.latest_progress_report_date_formatted }}</div>
                                <div class="text-sm">{{ afp.latest_progress_report_value | toCurrency }}</div>
                            </td>
                            <td :class="cellClass" class="align-top">
                                <div class="font-bold">{{ afp.latest_afp_date_formatted }}</div>
                                <div class="text-sm">{{ afp.latest_afp_value | toCurrency }}</div>
                            </td>
                            <td :class="cellClass" class="align-top">
                                <div class="flex flex-col gap-4">
                                    <template v-for="status in afp.statuses">
                                        <div v-if="show[statusLabelToDataKey(status.label)]">
                                            <div class="font-bold">{{ status.label }}</div>
                                            <div class="">{{ status.amount | toCurrency }}</div>
                                        </div>
                                    </template>
                                </div>
                            </td>
                        </tr>
                    </template>
                </ui-table>
                <div v-else class="px-4 py-16 text-center text-gray-400">
                    There has been no activity in accidents within the provided filters...
                </div>
            </ui-report-panel>
        </template>
    </ui-report>
</template>

<script>
import {groupBy, map, sortBy} from 'lodash'
import {Afp} from '@models'
import {parseMoment, toCurrency} from '@utils'
import AccountsSalesAfpsStat from "./AccountsSalesAfpsStat.vue";
import UiInput from "../ui/UiInput.vue";

export default {
    components: {UiInput, AccountsSalesAfpsStat},
    data() {
        return {
            applyFor: 0,
            uncertified: 0,
            retentionHeld: 0,
            applyForCount: 0,
            uncertifiedCount: 0,
            retentionHeldCount: 0,
            show: {
                applyFor: true,
                uncertified: true,
                retentionHeld: true,
            },
            query:'',
        }
    },
    methods: {
        async fetch() {
            let afps = await Afp.all()
            afps = afps.map(afp => this.parseAfp(afp))
            afps = groupBy(afps, afp => afp.due_group_key)
            afps = map(afps, (afps, key) => ({
                key,
                afps: sortBy(afps, afp => afp.diff_days),
                label: afps[0].due_group_label,
            }))
            let retentionPackageIds = []
            let applyForPackageIds = []
            let uncertifiedPackageIds = []
            afps.forEach(afp => {
                afp.afps.forEach(afp => {
                    afp.statuses.forEach(status => {
                        if ((status.label === 'Retention Held') && !retentionPackageIds.includes(afp.package_reference)) {
                            retentionPackageIds.push(afp.package_reference)
                            this.$data.retentionHeldCount++;
                            this.$data.retentionHeld += status.amount;
                        }
                        if ((status.label === 'To Apply For') && !applyForPackageIds.includes(afp.package_reference)) {
                            applyForPackageIds.push(afp.package_reference)
                            this.$data.applyForCount++;
                            this.$data.applyFor += status.amount;
                        }
                        if ((status.label === 'Uncertified') && !uncertifiedPackageIds.includes(afp.package_reference)) {
                            uncertifiedPackageIds.push(afp.package_reference)
                            this.$data.uncertifiedCount++;
                            this.$data.uncertified += status.amount;
                        }

                    })
                });
            });
            return afps
        },
        parseAfp(afp) {
            const dueDate = parseMoment(afp.application_due_date, 'YYYY-MM-DD')
            afp.diff_days = dueDate.diff(new Date(), 'days')
            afp.due_in = afp.diff_days + ' ' + (Math.abs(afp.diff_days) === 1 ? 'day' : 'days')
            afp.due_in = afp.diff_days === 0 ? 'today' : afp.due_in
            afp.due_in_class = afp.diff_days < 0 ? 'text-red-600' : 'text-green-600'
            let group
            if (!afp.has_future_dates) {
                group = {key: '0_missing', label: 'Missing Future Dates'}
            } else {
                group = this.getAfpGroup(afp.diff_days)
            }
            afp.due_group_key = group.key
            afp.due_group_label = group.label
            afp.application_due_date_formatted = parseMoment(afp.application_due_date, 'YYYY-MM-DD').format('DD/MM/YYYY')
            afp.latest_progress_report_date_formatted = parseMoment(afp.latest_progress_report_date, 'YYYY-MM-DD').format('DD/MM/YYYY')
            afp.latest_afp_date_formatted = parseMoment(afp.latest_afp_date, 'YYYY-MM-DD').format('DD/MM/YYYY')
            let tokens = [];
            tokens.push(afp.due_group_label,afp.application_due_date_formatted,afp.latest_afp_date_formatted,afp.application_due_date_formatted, afp.packageReference, afp.package_name)
            afp.tokens = tokens.join('|').toLowerCase();
            return afp
        },
        search() {
            const query = this.search.toLowerCase().split(/[^A-Za-z0-9.-]+/);
            if (query.length) {
                return items.filter((dd) => {
                    return query.every((token) => {
                        return dd.tokens.includes(token);
                    })
                });
            }
            return items;
        },
        getAfpGroup(diffDays) {
            if (diffDays < 0) return {key: '1_overdue', label: 'Overdue'}
            if (diffDays < 1) return {key: '2_today', label: 'Today'}
            if (diffDays < 15) return {key: '3_1_to_14_days', label: 'Due In 1 - 14 Days'}
            if (diffDays < 31) return {key: '4_14_to_30_days', label: 'Due In 14 - 30 Days'}
            return {key: '5_30_days_plus', label: 'Due 30 Days +'}
        },
        goTo(url) {
            window.location = url
        },
        formatTotal(total) {
            if (total === null) return total
            return toCurrency(total)
        },
        afpStatChanged(value) {
            this.$data.show[value.filter] = value.enabled
        },
        isNotFiltered(afp) {
            // if search, those not matching are skipped
            if (this.query) {
                const query = this.query.toLowerCase().split(/[^A-Za-z0-9.-]+/);
                if (query.length) {
                    let contains = query.every((token) => {
                        return afp.tokens.includes(token);
                    })
                    if (!contains) {
                        return false
                    }
                }
            }

            let data = afp.statuses.map(afp => afp.label);
            if (this.show.applyFor && (data.includes('To Apply For'))) {
                return true;
            }
            if (this.show.uncertified && (data.includes('Uncertified'))) {
                return true;
            }
            if (this.show.retentionHeld && (data.includes('Retention Held'))) {
                return true;
            }
            return false
        },
        statusLabelToDataKey(label) {
            if (label === 'To Apply For') return 'applyFor';
            if (label === 'Uncertified') return 'uncertified';
            if (label === 'Retention Held') return 'retentionHeld';
            return false
        }
    }
}
</script>
